import React, { useState } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Input, Textarea, Sel, Btn, Badge, Modal, Note } from "./ui";
import { Icon } from "./Icons";

// ═══════════════════════════════════════════════════════════════════════
// CLIENTES — CRM simples
// ═══════════════════════════════════════════════════════════════════════

const SEGMENTOS = [
  { id: "salao",     label: "Salão de Beleza" },
  { id: "barbearia", label: "Barbearia" },
  { id: "clinica",   label: "Clínica Estética" },
];

export default function Clientes({ clientes, setClientes }) {
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [editando, setEditando] = useState(null);
  const [novo, setNovo] = useState(false);

  const filtrados = clientes
    .filter(c => filtro === "todos" || c.segmento === filtro)
    .filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()));

  function salvar(c) {
    if (c.id) setClientes(clientes.map(x => x.id === c.id ? c : x));
    else setClientes([...clientes, { ...c, id: Date.now() }]);
    setEditando(null); setNovo(false);
  }
  function excluir(id) {
    if (window.confirm("Excluir este cliente?")) setClientes(clientes.filter(c => c.id !== id));
  }

  // Calcula potencial de recompra
  function potencialRecompra(ultimaCompra) {
    if (!ultimaCompra) return { label: "Sem histórico", color: T.textDim };
    const meses = (Date.now() - new Date(ultimaCompra).getTime()) / (1000 * 60 * 60 * 24 * 30);
    if (meses >= 18) return { label: "Alto", color: T.err };
    if (meses >= 12) return { label: "Médio", color: T.warn };
    return { label: "Baixo", color: T.ok };
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Clientes"
        subtitle={`${clientes.length} clientes cadastrados · acompanhamento e recompra`}
        icon={<Icon.client size={24} />}
        right={<Btn onClick={() => setNovo(true)} icon={<Icon.plus size={14} />}>Novo Cliente</Btn>}
      />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Input placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[{ id: "todos", label: "Todos" }, ...SEGMENTOS].map(s => (
            <button key={s.id} onClick={() => setFiltro(s.id)} style={{
              background: filtro === s.id ? T.brand : "transparent",
              color: filtro === s.id ? T.textInv : T.textSub,
              border: `1px solid ${filtro === s.id ? T.brand : T.borderMid}`,
              borderRadius: T.r.md, padding: "8px 14px",
              fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}>{s.label}</button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtrados.map(c => {
          const pot = potencialRecompra(c.ultimaCompra);
          const segLabel = SEGMENTOS.find(s => s.id === c.segmento)?.label || c.segmento;
          return (
            <Card key={c.id} style={{ padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                    <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.brand, letterSpacing: -0.2 }}>{c.nome}</div>
                    <Badge text={segLabel} tone="brand" />
                    <Badge text={`Recompra: ${pot.label}`} tone={pot.label === "Alto" ? "err" : pot.label === "Médio" ? "warn" : "ok"} />
                  </div>
                  <div style={{ fontSize: 12, color: T.textSub, display: "flex", flexDirection: "column", gap: 3 }}>
                    {c.responsavel && <div><Icon.user size={11} /> {c.responsavel}</div>}
                    {c.telefone && <div><Icon.phone size={11} /> {c.telefone}</div>}
                    {c.cidade && <div><Icon.pin size={11} /> {c.cidade}</div>}
                    {c.ultimaCompra && <div>Última compra: <b>{new Date(c.ultimaCompra).toLocaleDateString("pt-BR")}</b></div>}
                    {c.valorHistorico > 0 && <div>Histórico: <b style={{ color: T.brand }}>{fmt.brl(c.valorHistorico)}</b></div>}
                  </div>
                  {c.obs && (
                    <div style={{ fontSize: 11, color: T.textSub, marginTop: 8, fontStyle: "italic", borderLeft: `2px solid ${T.border}`, paddingLeft: 10 }}>
                      {c.obs}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                  <Btn variant="ghost" icon={<Icon.edit size={12} />} onClick={() => setEditando(c)}>Editar</Btn>
                  <Btn variant="danger" icon={<Icon.trash size={12} />} onClick={() => excluir(c.id)}>Excluir</Btn>
                </div>
              </div>
            </Card>
          );
        })}

        {filtrados.length === 0 && (
          <Card>
            <div style={{ textAlign: "center", padding: 40, color: T.textDim, fontSize: 13 }}>
              {busca || filtro !== "todos" ? "Nenhum cliente encontrado com os filtros atuais." : "Nenhum cliente cadastrado. Clique em 'Novo Cliente' para começar."}
            </div>
          </Card>
        )}
      </div>

      {(editando || novo) && (
        <ClienteForm
          cliente={editando || { nome: "", segmento: "salao", responsavel: "", telefone: "", cidade: "", ultimaCompra: "", valorHistorico: 0, wixUrl: "", obs: "" }}
          onSave={salvar}
          onCancel={() => { setEditando(null); setNovo(false); }}
        />
      )}
    </div>
  );
}

function ClienteForm({ cliente, onSave, onCancel }) {
  const [c, setC] = useState(cliente);

  return (
    <Modal title={cliente.id ? "Editar Cliente" : "Novo Cliente"} onClose={onCancel} width={520}>
      <Input label="Nome do estabelecimento" value={c.nome} onChange={e => setC({ ...c, nome: e.target.value })} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Sel label="Segmento" value={c.segmento} onChange={e => setC({ ...c, segmento: e.target.value })}>
          {SEGMENTOS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </Sel>
        <Input label="Responsável" value={c.responsavel} onChange={e => setC({ ...c, responsavel: e.target.value })} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Telefone" value={c.telefone} onChange={e => setC({ ...c, telefone: e.target.value })} />
        <Input label="Cidade, UF" value={c.cidade} onChange={e => setC({ ...c, cidade: e.target.value })} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Última compra" type="date" value={c.ultimaCompra} onChange={e => setC({ ...c, ultimaCompra: e.target.value })} />
        <Input label="Histórico (R$)" type="number" value={c.valorHistorico} onChange={e => setC({ ...c, valorHistorico: Number(e.target.value) })} />
      </div>
      <Input label="URL no Wix (opcional)" placeholder="https://..." value={c.wixUrl} onChange={e => setC({ ...c, wixUrl: e.target.value })} />
      <Textarea label="Observações" value={c.obs} onChange={e => setC({ ...c, obs: e.target.value })} rows={3} />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
        <Btn onClick={() => onSave(c)} icon={<Icon.check size={14} />}>Salvar</Btn>
      </div>
    </Modal>
  );
}
